/**
 * SingleSelect — 단일 선택 드롭다운 (Radix 미사용)
 * MultiSelect와 동일하게 스크롤 잠금 없음 → 레이아웃 시프트 방지
 * Dropdown menu is rendered via createPortal to escape overflow:hidden/clip parents.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Check, ChevronDown } from 'lucide-react';

export interface SingleSelectOption {
  value: string;
  label: string;
  /** If provided, shown in the trigger instead of label (dropdown still shows label) */
  triggerLabel?: string;
  /** FontAwesome 아이콘 이름 (fa- 제외, 예: 'check', 'envelope-o') */
  icon?: string;
  /** Arbitrary ReactNode rendered before the label (overrides icon) */
  prefix?: React.ReactNode;
  /** Group label — items with the same group are visually separated under a header */
  group?: string;
}

interface SingleSelectProps {
  options: SingleSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** 트리거 버튼에 추가할 className (너비·높이 오버라이드용) */
  triggerClassName?: string;
  disabled?: boolean;
  /** 옵션 8개 초과 시 검색 표시 (기본 true) */
  searchable?: boolean;
  /** 트리거 텍스트 앞에 표시할 아이콘 */
  prefixIcon?: ReactNode;
  /** 'sm' → h-[25px], compact padding/text (ToolbarOrderSelect compact와 동일 높이) */
  size?: 'default' | 'sm';
  /** 드롭다운 메뉴 최소 너비(px) — 트리거가 좁아도 옵션 라벨이 잘리지 않도록 (기본 128) */
  menuMinWidth?: number;
}

export default function SingleSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className,
  triggerClassName,
  disabled = false,
  searchable,
  prefixIcon,
  size = 'default',
  menuMinWidth = 128,
}: SingleSelectProps) {
  const showSearch = searchable ?? options.length > 8;
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  // Recalculate portal menu position from the trigger bounding rect
  const updatePos = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  // Click-outside: close when clicking outside both the trigger and the portal menu
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!menuRef.current || !menuRef.current.contains(e.target as Node))
      ) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // On open: compute position, focus search input, and track scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePos();
    setTimeout(() => filterRef.current?.focus(), 50);
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updatePos]);

  const filteredOptions = useMemo(() => {
    if (!showSearch || !filter.trim()) return options;
    const q = filter.trim().toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, filter, showSearch]);

  const [highlightIdx, setHighlightIdx] = useState(0);
  useEffect(() => setHighlightIdx(0), [filter]);

  const selectedOption = options.find((o) => o.value === value);
  const displayText = selectedOption?.triggerLabel ?? selectedOption?.label ?? placeholder;

  const select = useCallback(
    (val: string) => {
      onChange(val);
      setOpen(false);
      setFilter('');
    },
    [onChange]
  );

  const listRef = useRef<HTMLDivElement>(null);
  const scrollToHighlight = useCallback((idx: number) => {
    const container = listRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-ss-item]');
    items[idx]?.scrollIntoView({ block: 'nearest' });
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <div
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`select-trigger ${size === 'sm' ? 'select-trigger--sm' : ''} ${open ? 'select-trigger--open' : ''} ${disabled ? 'select-trigger--disabled' : ''} ${triggerClassName ?? ''}`}
      >
        <span className={`select-trigger__text ${!value ? 'select-trigger__text--empty' : ''}`}>
          {prefixIcon}
          {selectedOption?.prefix ?? (selectedOption?.icon && (
            <i className={`fa fa-${selectedOption.icon} text-[11px] text-ggc-text-gray`} />
          ))}
          {displayText}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-ggc-text-gray-light transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {open && createPortal(
        <FocusScope trapped={false}>
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: Math.max(menuPos.width, menuMinWidth),
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
          className="rounded-md border border-border bg-white shadow-ggc-lg"
        >
          {showSearch && (
            <div className="border-b border-border p-2">
              <input
                ref={filterRef}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightIdx((prev) => {
                      const next = Math.min(prev + 1, filteredOptions.length - 1);
                      scrollToHighlight(next);
                      return next;
                    });
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightIdx((prev) => {
                      const next = Math.max(prev - 1, 0);
                      scrollToHighlight(next);
                      return next;
                    });
                  } else if (e.key === 'Enter' && filteredOptions.length > 0) {
                    e.preventDefault();
                    select(filteredOptions[highlightIdx]?.value ?? filteredOptions[0].value);
                  } else if (e.key === 'Escape') {
                    setOpen(false);
                    setFilter('');
                  }
                }}
                placeholder="Search..."
                className="w-full rounded-ggc-sm border border-input px-2 py-1 text-xs outline-none focus:border-ggc-cta-blue"
              />
            </div>
          )}
          <div ref={listRef} className="max-h-60 overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-[12px] text-ggc-text-gray">No options found</div>
            ) : (() => {
              const items: React.ReactNode[] = [];
              let lastGroup: string | undefined = undefined;
              filteredOptions.forEach((o, i) => {
                if (o.group !== undefined && o.group !== lastGroup) {
                  items.push(
                    <div key={`grp-${o.group}`} className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ggc-text-gray first:pt-1">
                      {o.group}
                    </div>
                  );
                  lastGroup = o.group;
                }
                const isSelected = value === o.value;
                const isHighlighted = i === highlightIdx;
                items.push(
                  <button
                    key={o.value}
                    data-ss-item
                    type="button"
                    onClick={() => select(o.value)}
                    onMouseEnter={() => setHighlightIdx(i)}
                    className={`flex w-full items-center gap-2 px-1 py-1 text-left text-[11px] transition ${
                      isHighlighted ? 'bg-muted/60' : 'hover:bg-muted/40'
                    } ${isSelected ? 'bg-[var(--primary-50)] text-[var(--primary-600)]' : 'text-foreground'} rounded-sm`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-ggc-sm border ${
                        isSelected ? 'border-[var(--primary-600)] bg-[var(--primary-600)] text-white' : 'border-ggc-border-gray-dark'
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    {o.prefix ?? (o.icon && (
                      <i className={`fa fa-${o.icon} w-3.5 text-center text-[11px] text-ggc-text-gray shrink-0`} />
                    ))}
                    <span className="flex-1 truncate">{o.label}</span>
                  </button>
                );
              });
              return items;
            })()}
          </div>
        </div>
        </FocusScope>,
        document.body
      )}
    </div>
  );
}
